import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { leaveAdvancedAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, ScrollText, Plus, Edit, Trash2, Sparkles, Filter, Calendar, ShieldCheck } from 'lucide-react';

const CATEGORY_COLORS = { paid: 'default', unpaid: 'destructive', wfh: 'secondary', special: 'outline' };

const LeavePolicies = () => {
  const { user } = useAuth();
  const allowed = !user || ['super_admin', 'hr_admin'].includes(user.role);

  const [types, setTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterCode, setFilterCode] = useState('all');
  const [editPolicy, setEditPolicy] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [seedDialog, setSeedDialog] = useState(false);
  const [seedCompany, setSeedCompany] = useState('global');
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, c, p] = await Promise.all([
        leaveAdvancedAPI.listTypesMaster(),
        companiesAPI.getAll(),
        leaveAdvancedAPI.listPolicies(),
      ]);
      setTypes(t.data);
      setCompanies(c.data);
      setPolicies(p.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (allowed) fetchAll(); /* eslint-disable-next-line */ }, [allowed]);

  if (!allowed) return <Navigate to="/" replace />;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const filtered = policies.filter(p => {
    if (filterCompany === 'all') { /* show all */ }
    else if (filterCompany === 'global' && p.scope?.company_id) return false;
    else if (filterCompany !== 'global' && filterCompany !== 'all' && p.scope?.company_id !== filterCompany) return false;
    if (filterCode !== 'all' && p.leave_code !== filterCode) return false;
    return true;
  });

  const openCreate = (typeCode) => {
    const tpl = types.find(t => t.code === typeCode);
    setEditPolicy({
      leave_code: typeCode,
      _typeLabel: tpl?.label,
      _isNew: true,
      scope: { country: null, company_id: null, department: null, designation: null, grade: null, gender: null, min_tenure_months: 0, employment_type: null, exclude_probation: false, exclude_contract: false },
      accrual: { frequency: 'yearly', days_per_period: 0, prorate_first_year: true, max_balance_cap: null, auto_credit_on: '01-01' },
      balance: { carry_forward_max: 0, carry_forward_expiry_days: 0, auto_lapse: true, allow_negative: false, negative_max_days: 0, advance_allowed: false, advance_max_days: 0 },
      encashment: { is_encashable: false, max_encashable_days_per_year: 0 },
      approval: { levels: ['manager', 'hr'], parallel: false, sla_hours: 48, auto_escalate_after_hours: 72, allow_delegation: true },
      freeze_periods: [],
      planned_rules: typeCode === 'planned' ? { min_notice_days: 7, backup_employee_required: true, handover_notes_required: true } : null,
      sick_rules: typeCode === 'sick' || typeCode === 'injury' ? { medical_cert_required_after_days: 2, hr_escalation_after_days: 5, chronic_pattern_threshold: 3, chronic_window_days: 30, max_consecutive_days: 30 } : null,
      wfh_rules: typeCode === 'wfh' ? { max_per_month: 8, max_consecutive: 5, require_gps_login: true, geo_restriction_km: 100 } : null,
      is_active: true,
    });
    setEditorOpen(true);
  };

  const openEdit = (p) => {
    setEditPolicy({ ...p, _typeLabel: types.find(t => t.code === p.leave_code)?.label });
    setEditorOpen(true);
  };

  const saveEditor = async () => {
    if (!editPolicy) return;
    setSubmitting(true);
    try {
      const payload = { ...editPolicy };
      delete payload._typeLabel; delete payload._isNew; delete payload.id; delete payload.created_at; delete payload.created_by; delete payload.updated_at; delete payload.updated_by;
      if (editPolicy._isNew) await leaveAdvancedAPI.createPolicy(payload);
      else await leaveAdvancedAPI.updatePolicy(editPolicy.id, payload);
      toast.success(editPolicy._isNew ? 'Policy created' : 'Policy updated');
      setEditorOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSubmitting(false); }
  };

  const deletePolicy = async (id) => {
    if (!window.confirm('Delete this policy? Employees will fall back to system defaults.')) return;
    try { await leaveAdvancedAPI.deletePolicy(id); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const seedDefaults = async () => {
    try {
      const r = await leaveAdvancedAPI.seedDefaults(seedCompany && seedCompany !== 'global' ? seedCompany : null);
      toast.success(`Seeded ${r.data.seeded} policies (${r.data.skipped} skipped)`);
      setSeedDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const companyName = (id) => id ? (companies.find(c => c.id === id)?.name || id.slice(0, 8)) : 'Global Default';

  const updField = (path, value) => {
    setEditPolicy(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="leave-policies-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ScrollText />Leave Policies</h1>
          <p className="text-muted-foreground text-sm">Phase 12a — Define accrual, balance, approval &amp; rule logic for 20 leave types · scope by company / department / designation / gender / tenure.</p>
        </div>
        <Button onClick={() => setSeedDialog(true)} variant="outline" data-testid="seed-defaults-btn"><Sparkles size={14} className="mr-1" />Seed Defaults</Button>
      </div>

      <Tabs defaultValue="catalog">
        <TabsList>
          <TabsTrigger value="catalog" data-testid="tab-catalog">Leave Types ({types.length})</TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies">Policies ({policies.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Code</TableHead><TableHead>Label</TableHead><TableHead>Category</TableHead>
                  <TableHead>Active Policies</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {types.map(t => {
                    const ps = policies.filter(p => p.leave_code === t.code);
                    return (
                      <TableRow key={t.code} data-testid={`leave-type-row-${t.code}`}>
                        <TableCell><div className="w-3 h-3 rounded" style={{background: t.color}} /></TableCell>
                        <TableCell className="font-mono text-xs">{t.code}</TableCell>
                        <TableCell><strong>{t.label}</strong></TableCell>
                        <TableCell><Badge variant={CATEGORY_COLORS[t.category]}>{t.category}</Badge></TableCell>
                        <TableCell>
                          {ps.length === 0 ? <span className="text-muted-foreground italic text-xs">none</span> : (
                            <div className="flex flex-wrap gap-1">
                              {ps.map(p => <Badge key={p.id} variant="outline" className="text-xs cursor-pointer" onClick={() => openEdit(p)}>{companyName(p.scope?.company_id)}</Badge>)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell><Button size="sm" variant="outline" onClick={() => openCreate(t.code)} data-testid={`add-policy-${t.code}`}><Plus size={12} className="mr-1" />Add Policy</Button></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Filter size={14} />Filters</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Select value={filterCompany} onValueChange={setFilterCompany}>
                  <SelectTrigger className="w-48" data-testid="filter-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All scopes</SelectItem>
                    <SelectItem value="global">Global only</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterCode} onValueChange={setFilterCode}>
                  <SelectTrigger className="w-48" data-testid="filter-code"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {types.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Leave Type</TableHead><TableHead>Scope</TableHead>
                  <TableHead>Accrual</TableHead><TableHead>Carry Fwd</TableHead>
                  <TableHead>Approval</TableHead><TableHead>Active</TableHead><TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No policies match filters.</TableCell></TableRow>}
                  {filtered.map(p => {
                    const t = types.find(x => x.code === p.leave_code) || {};
                    return (
                      <TableRow key={p.id} data-testid={`policy-row-${p.id}`}>
                        <TableCell><Badge variant="outline" style={{borderColor: t.color, color: t.color}}>{t.label}</Badge></TableCell>
                        <TableCell className="text-xs">
                          <div>{companyName(p.scope?.company_id)}</div>
                          <div className="text-muted-foreground">
                            {[p.scope?.department, p.scope?.designation, p.scope?.gender, p.scope?.min_tenure_months ? `≥${p.scope.min_tenure_months}mo` : null].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{p.accrual?.frequency} · {p.accrual?.days_per_period}d</TableCell>
                        <TableCell className="text-xs">{p.balance?.carry_forward_max}d</TableCell>
                        <TableCell className="text-xs">{(p.approval?.levels || []).join(' → ')}</TableCell>
                        <TableCell><Switch checked={p.is_active !== false} onCheckedChange={async (v) => { await leaveAdvancedAPI.updatePolicy(p.id, { leave_code: p.leave_code, is_active: v }); fetchAll(); }} /></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-policy-${p.id}`}><Edit size={12} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => deletePolicy(p.id)}><Trash2 size={12} className="text-red-600" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editPolicy?._isNew ? 'New' : 'Edit'} Policy — {editPolicy?._typeLabel}</DialogTitle></DialogHeader>
          {editPolicy && (
            <Tabs defaultValue="scope">
              <TabsList>
                <TabsTrigger value="scope">Scope</TabsTrigger>
                <TabsTrigger value="accrual">Accrual</TabsTrigger>
                <TabsTrigger value="balance">Balance</TabsTrigger>
                <TabsTrigger value="approval">Approval</TabsTrigger>
                {editPolicy.leave_code === 'planned' && <TabsTrigger value="planned">Planned</TabsTrigger>}
                {(editPolicy.leave_code === 'sick' || editPolicy.leave_code === 'injury') && <TabsTrigger value="sick">Sick</TabsTrigger>}
                {editPolicy.leave_code === 'wfh' && <TabsTrigger value="wfh">WFH</TabsTrigger>}
              </TabsList>

              <TabsContent value="scope">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label>Company</Label>
                    <Select value={editPolicy.scope?.company_id || 'all'} onValueChange={(v) => updField('scope.company_id', v === 'all' ? null : v)}>
                      <SelectTrigger data-testid="scope-company"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">— Global (all companies) —</SelectItem>
                        {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Country</Label><Input value={editPolicy.scope?.country || ''} onChange={(e) => updField('scope.country', e.target.value || null)} placeholder="UAE / India / ..." /></div>
                  <div><Label>Department</Label><Input value={editPolicy.scope?.department || ''} onChange={(e) => updField('scope.department', e.target.value || null)} placeholder="(any)" /></div>
                  <div><Label>Designation</Label><Input value={editPolicy.scope?.designation || ''} onChange={(e) => updField('scope.designation', e.target.value || null)} placeholder="(any)" /></div>
                  <div><Label>Grade / Band</Label><Input value={editPolicy.scope?.grade || ''} onChange={(e) => updField('scope.grade', e.target.value || null)} placeholder="(any)" /></div>
                  <div><Label>Gender</Label>
                    <Select value={editPolicy.scope?.gender || 'any'} onValueChange={(v) => updField('scope.gender', v === 'any' ? null : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="male">Male only</SelectItem>
                        <SelectItem value="female">Female only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Min. Tenure (months)</Label><Input type="number" value={editPolicy.scope?.min_tenure_months || 0} onChange={(e) => updField('scope.min_tenure_months', parseInt(e.target.value) || 0)} /></div>
                  <div><Label>Employment Type</Label>
                    <Select value={editPolicy.scope?.employment_type || 'any'} onValueChange={(v) => updField('scope.employment_type', v === 'any' ? null : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="permanent">Permanent</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="part_time">Part Time</SelectItem>
                        <SelectItem value="intern">Intern</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 flex items-center gap-4 pt-2 border-t">
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.scope?.exclude_probation || false} onCheckedChange={(v) => updField('scope.exclude_probation', v)} /><Label>Exclude employees on probation</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.scope?.exclude_contract || false} onCheckedChange={(v) => updField('scope.exclude_contract', v)} /><Label>Exclude contract employees</Label></div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="accrual">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label>Frequency</Label>
                    <Select value={editPolicy.accrual?.frequency || 'yearly'} onValueChange={(v) => updField('accrual.frequency', v)}>
                      <SelectTrigger data-testid="accrual-freq"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="none">None (manual / earned)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Days per Period</Label><Input type="number" step="0.25" value={editPolicy.accrual?.days_per_period || 0} onChange={(e) => updField('accrual.days_per_period', parseFloat(e.target.value) || 0)} data-testid="accrual-days" /></div>
                  <div><Label>Max Balance Cap</Label><Input type="number" value={editPolicy.accrual?.max_balance_cap || ''} onChange={(e) => updField('accrual.max_balance_cap', e.target.value ? parseFloat(e.target.value) : null)} placeholder="(no cap)" /></div>
                  <div><Label>Auto-Credit On (MM-DD)</Label><Input value={editPolicy.accrual?.auto_credit_on || '01-01'} onChange={(e) => updField('accrual.auto_credit_on', e.target.value)} /></div>
                  <div className="col-span-2 flex items-center gap-2"><Switch checked={editPolicy.accrual?.prorate_first_year !== false} onCheckedChange={(v) => updField('accrual.prorate_first_year', v)} /><Label>Pro-rate first year (joiner)</Label></div>
                </div>
              </TabsContent>

              <TabsContent value="balance">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label>Carry Forward Max (days)</Label><Input type="number" value={editPolicy.balance?.carry_forward_max || 0} onChange={(e) => updField('balance.carry_forward_max', parseFloat(e.target.value) || 0)} /></div>
                  <div><Label>Carry Forward Expiry (days)</Label><Input type="number" value={editPolicy.balance?.carry_forward_expiry_days || 0} onChange={(e) => updField('balance.carry_forward_expiry_days', parseInt(e.target.value) || 0)} /></div>
                  <div><Label>Advance Max (days)</Label><Input type="number" value={editPolicy.balance?.advance_max_days || 0} onChange={(e) => updField('balance.advance_max_days', parseFloat(e.target.value) || 0)} disabled={!editPolicy.balance?.advance_allowed} /></div>
                  <div><Label>Negative Max (days)</Label><Input type="number" value={editPolicy.balance?.negative_max_days || 0} onChange={(e) => updField('balance.negative_max_days', parseFloat(e.target.value) || 0)} disabled={!editPolicy.balance?.allow_negative} /></div>
                  <div className="col-span-2 flex items-center gap-4 pt-2 border-t flex-wrap">
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.balance?.auto_lapse !== false} onCheckedChange={(v) => updField('balance.auto_lapse', v)} /><Label>Auto-lapse unused</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.balance?.allow_negative || false} onCheckedChange={(v) => updField('balance.allow_negative', v)} /><Label>Allow negative balance</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.balance?.advance_allowed || false} onCheckedChange={(v) => updField('balance.advance_allowed', v)} /><Label>Advance leave allowed</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.encashment?.is_encashable || false} onCheckedChange={(v) => updField('encashment.is_encashable', v)} /><Label>Encashable</Label></div>
                  </div>
                  {editPolicy.encashment?.is_encashable && (
                    <div className="col-span-2"><Label>Max Encashable Days/Year</Label><Input type="number" value={editPolicy.encashment?.max_encashable_days_per_year || 0} onChange={(e) => updField('encashment.max_encashable_days_per_year', parseFloat(e.target.value) || 0)} /></div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="approval">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="col-span-2">
                    <Label>Approval Chain (in order)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['manager', 'hr', 'department_head', 'payroll_admin', 'finance', 'super_admin'].map(role => {
                        const idx = (editPolicy.approval?.levels || []).indexOf(role);
                        const active = idx >= 0;
                        return (
                          <Badge key={role} variant={active ? 'default' : 'outline'} className="cursor-pointer" onClick={() => {
                            const levels = [...(editPolicy.approval?.levels || [])];
                            if (active) levels.splice(idx, 1); else levels.push(role);
                            updField('approval.levels', levels);
                          }}>{active && `${idx + 1}. `}{role.replace('_', ' ')}</Badge>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Click to toggle. Order = approval sequence.</p>
                  </div>
                  <div className="flex items-center gap-2"><Switch checked={editPolicy.approval?.parallel || false} onCheckedChange={(v) => updField('approval.parallel', v)} /><Label>Parallel approval</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={editPolicy.approval?.allow_delegation !== false} onCheckedChange={(v) => updField('approval.allow_delegation', v)} /><Label>Allow delegation</Label></div>
                  <div><Label>SLA (hours)</Label><Input type="number" value={editPolicy.approval?.sla_hours || 48} onChange={(e) => updField('approval.sla_hours', parseInt(e.target.value) || 48)} /></div>
                  <div><Label>Auto-Escalate After (hours)</Label><Input type="number" value={editPolicy.approval?.auto_escalate_after_hours || 72} onChange={(e) => updField('approval.auto_escalate_after_hours', parseInt(e.target.value) || 72)} /></div>
                </div>
              </TabsContent>

              <TabsContent value="planned">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label>Min Notice (days)</Label><Input type="number" value={editPolicy.planned_rules?.min_notice_days || 0} onChange={(e) => updField('planned_rules.min_notice_days', parseInt(e.target.value) || 0)} /></div>
                  <div><Label>Team Overlap Max (%)</Label><Input type="number" value={editPolicy.planned_rules?.team_overlap_max_percent || 0} onChange={(e) => updField('planned_rules.team_overlap_max_percent', parseInt(e.target.value) || 0)} /></div>
                  <div><Label>Dept Min Staffing (%)</Label><Input type="number" value={editPolicy.planned_rules?.department_min_staffing_percent || 0} onChange={(e) => updField('planned_rules.department_min_staffing_percent', parseInt(e.target.value) || 0)} /></div>
                  <div className="col-span-2 flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.planned_rules?.backup_employee_required !== false} onCheckedChange={(v) => updField('planned_rules.backup_employee_required', v)} /><Label>Backup employee required</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.planned_rules?.handover_notes_required !== false} onCheckedChange={(v) => updField('planned_rules.handover_notes_required', v)} /><Label>Handover notes required</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.planned_rules?.calendar_conflict_check !== false} onCheckedChange={(v) => updField('planned_rules.calendar_conflict_check', v)} /><Label>Calendar conflict check</Label></div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="sick">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label>Medical Cert Required After (days)</Label><Input type="number" value={editPolicy.sick_rules?.medical_cert_required_after_days || 2} onChange={(e) => updField('sick_rules.medical_cert_required_after_days', parseInt(e.target.value) || 2)} /></div>
                  <div><Label>HR Escalation After (days)</Label><Input type="number" value={editPolicy.sick_rules?.hr_escalation_after_days || 5} onChange={(e) => updField('sick_rules.hr_escalation_after_days', parseInt(e.target.value) || 5)} /></div>
                  <div><Label>Chronic Threshold (occurrences)</Label><Input type="number" value={editPolicy.sick_rules?.chronic_pattern_threshold || 3} onChange={(e) => updField('sick_rules.chronic_pattern_threshold', parseInt(e.target.value) || 3)} /></div>
                  <div><Label>Chronic Window (days)</Label><Input type="number" value={editPolicy.sick_rules?.chronic_window_days || 30} onChange={(e) => updField('sick_rules.chronic_window_days', parseInt(e.target.value) || 30)} /></div>
                  <div><Label>Max Consecutive Days</Label><Input type="number" value={editPolicy.sick_rules?.max_consecutive_days || 30} onChange={(e) => updField('sick_rules.max_consecutive_days', parseInt(e.target.value) || 30)} /></div>
                </div>
              </TabsContent>

              <TabsContent value="wfh">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label>Max Per Month</Label><Input type="number" value={editPolicy.wfh_rules?.max_per_month || 8} onChange={(e) => updField('wfh_rules.max_per_month', parseInt(e.target.value) || 8)} /></div>
                  <div><Label>Max Consecutive</Label><Input type="number" value={editPolicy.wfh_rules?.max_consecutive || 5} onChange={(e) => updField('wfh_rules.max_consecutive', parseInt(e.target.value) || 5)} /></div>
                  <div><Label>Geo Restriction (km)</Label><Input type="number" value={editPolicy.wfh_rules?.geo_restriction_km || 100} onChange={(e) => updField('wfh_rules.geo_restriction_km', parseInt(e.target.value) || 100)} /></div>
                  <div><Label>Team Coverage Min (%)</Label><Input type="number" value={editPolicy.wfh_rules?.team_coverage_min_percent || 50} onChange={(e) => updField('wfh_rules.team_coverage_min_percent', parseInt(e.target.value) || 50)} /></div>
                  <div className="col-span-2 flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.wfh_rules?.require_gps_login !== false} onCheckedChange={(v) => updField('wfh_rules.require_gps_login', v)} /><Label>Require GPS login</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={editPolicy.wfh_rules?.vpn_verification || false} onCheckedChange={(v) => updField('wfh_rules.vpn_verification', v)} /><Label>VPN verification</Label></div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveEditor} disabled={submitting} data-testid="save-policy-btn">
              {submitting && <Loader2 size={14} className="animate-spin mr-1" />}<ShieldCheck size={14} className="mr-1" />Save Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed Defaults Dialog */}
      <Dialog open={seedDialog} onOpenChange={setSeedDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles size={18} />Seed Default Policies</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Create sensible default policies for all 20 leave types. Already-existing policies will be skipped.</p>
            <div>
              <Label>Scope</Label>
              <Select value={seedCompany} onValueChange={setSeedCompany}>
                <SelectTrigger data-testid="seed-company"><SelectValue placeholder="(Global default)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">— Global (apply to all companies) —</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.country})</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Country-specific defaults are auto-applied when a company is selected (e.g., UAE EOSB rules for annual leave).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedDialog(false)}>Cancel</Button>
            <Button onClick={seedDefaults} data-testid="confirm-seed-btn"><Sparkles size={14} className="mr-1" />Seed 20 Defaults</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavePolicies;
